/**
 * Address line-1 input with Azure Maps autocomplete (proxied via the API).
 *
 * Drop-in for the address line-1 field: as the user types, a debounced search
 * surfaces ranked suggestions; picking one fills the whole address (line1 +
 * city/state/zip + lat/lng) via `onSelect`. Degrades to a plain text input
 * when the provider key isn't configured (the search just returns nothing).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import type { AddressSuggestion } from '@vari-crew/schemas';
import { useAddressSearch } from '@/api/geo';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

export function AddressAutocomplete({
  id,
  value,
  onChange,
  onSelect,
  placeholder,
  className,
}: {
  id?: string;
  value: string;
  onChange: (line1: string) => void;
  onSelect: (s: AddressSuggestion) => void;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // Debounce what we send to the API so a typeahead doesn't fire per keystroke.
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  const { data, isFetching } = useAddressSearch(open ? debounced : '');
  const suggestions = data?.suggestions ?? [];
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function choose(s: AddressSuggestion) {
    onSelect(s);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a <= 0 ? suggestions.length - 1 : a - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      choose(suggestions[active]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const showList = open && (suggestions.length > 0 || (debounced.trim().length >= 3 && isFetching));

  return (
    <div ref={wrapRef} className="relative">
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-md border border-line bg-bg shadow-lg text-sm"
        >
          {suggestions.length === 0 && isFetching && (
            <li className="px-3 py-2 text-ink-faint">Searching…</li>
          )}
          {suggestions.map((s, i) => (
            <li
              key={`${s.label}-${i}`}
              role="option"
              aria-selected={i === active}
              // onMouseDown (not onClick) so the pick registers before blur.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(s);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                'flex items-start gap-2 px-3 py-2 cursor-pointer',
                i === active ? 'bg-fill-soft' : 'hover:bg-fill-soft',
              )}
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-ink-faint" />
              {s.poi_name ? (
                <span className="min-w-0">
                  <span className="block font-medium truncate">{s.poi_name}</span>
                  <span className="block text-xs text-ink-faint truncate">{s.label}</span>
                </span>
              ) : (
                <span className="min-w-0">{s.label}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
